import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import {
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Modal,
  Row,
  Table,
} from 'react-bootstrap';
import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';
import { getTotalScorePercentage, getBarColor } from '../../util/util';
import type { AssessmentReport } from '../../hooks/useGetAssessmentReport';
import ScoreIcon from '../common/ScoreIcon';
import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { getPreambleText } from './util';
import { getLocalizedString } from '../../util/getLocalizedString';
import logo from '../../assets/LogoNavbar.png';
import { captureSupabaseError } from '../../lib/sentry';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  type ChartOptions,
} from 'chart.js';
import '../../styles/print.scss';

interface AssessmentReportDisplayNewProps {
  report: AssessmentReport;
  isPublished?: boolean;
}

const isPrinting =
  new URLSearchParams(window.location.search).get('print') === 'true';
const PDF_RENDER_SCALE = 1.25;
const PDF_IMAGE_QUALITY = 0.72;

const csvHeaders = [
  'record_type',
  'section_title',
  'item_label',
  'item_key',
  'value',
];
type CsvRow = Array<string | number | boolean>;

const reportStyles = {
  shell: {
    background:
      'linear-gradient(180deg, #f4f7fb 0%, #eef3f8 24%, #f8fafc 100%)',
    minHeight: '100vh',
  },
  topBar: {
    background: 'rgba(10, 18, 32, 0.94)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  heroCard: {
    border: '1px solid #dbe4ee',
    borderRadius: '24px',
    boxShadow: '0 18px 50px rgba(15, 23, 42, 0.08)',
    overflow: 'hidden',
    background: '#ffffff',
  },
  heroHeader: {
    background:
      'linear-gradient(135deg, #0f172a 0%, #183b5c 58%, #235784 100%)',
    color: '#f8fafc',
  },
  eyebrow: {
    fontSize: '0.78rem',
    letterSpacing: '0.14em',
    textTransform: 'uppercase' as const,
    color: 'rgba(241,245,249,0.7)',
    fontWeight: 700,
  },
  metricCard: {
    background: '#ffffff',
    border: '1px solid #d9e3ee',
    borderRadius: '18px',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
  },
  metricLabel: {
    fontSize: '0.76rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    color: '#64748b',
    fontWeight: 700,
  },
  sectionCard: {
    border: '1px solid #dbe4ee',
    borderRadius: '22px',
    boxShadow: '0 14px 40px rgba(15, 23, 42, 0.06)',
    overflow: 'hidden',
    background: '#ffffff',
  },
  sectionHeader: {
    background: 'linear-gradient(180deg, #f8fbfd 0%, #eef4f8 100%)',
    borderBottom: '1px solid #dbe4ee',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    background: '#e8f1f8',
    color: '#18456b',
    borderRadius: '999px',
    padding: '0.35rem 0.7rem',
    fontSize: '0.78rem',
    fontWeight: 700,
  },
  tableShell: {
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    overflow: 'hidden',
  },
  subSectionTitle: {
    fontSize: '0.88rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    fontWeight: 700,
    color: '#475569',
  },
} satisfies Record<string, React.CSSProperties>;

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString() : 'Not available';

const SummaryMetric = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}) => (
  <Card style={reportStyles.metricCard} className="h-100">
    <Card.Body className="p-3 p-md-4">
      <div style={reportStyles.metricLabel}>{label}</div>
      <div
        className="fw-semibold mt-2"
        style={{
          fontSize: '1.4rem',
          color: accent || '#0f172a',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
    </Card.Body>
  </Card>
);

const getCsvCellValue = (value: unknown) => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const escapeCsvValue = (value: unknown) => {
  const normalizedValue = getCsvCellValue(value).replace(/"/g, '""');
  return `"${normalizedValue}"`;
};

const buildCsvRows = (report: AssessmentReport) => {
  const rows: CsvRow[] = [];

  rows.push([
    'meta',
    '',
    'template_name',
    'template_name',
    report.meta.template_name,
  ]);
  rows.push([
    'meta',
    '',
    'supplier_name',
    'supplier_name',
    report.meta.supplier_name,
  ]);
  rows.push([
    'meta',
    '',
    'overall_percentage',
    'overall_percentage',
    report.meta.overall_percentage,
  ]);
  rows.push([
    'meta',
    '',
    'total_score',
    'total_score',
    report.meta.total_score,
  ]);
  rows.push(['meta', '', 'max_score', 'max_score', report.meta.max_score]);
  rows.push([
    'meta',
    '',
    'assessment_id',
    'assessment_id',
    report.meta.assessment_id,
  ]);
  rows.push([
    'meta',
    '',
    'supplier_id',
    'supplier_id',
    report.meta.supplier_id,
  ]);
  rows.push(['meta', '', 'created', 'created', report.created ?? '']);

  report.sections.forEach((section) => {
    rows.push(['section', section.title, 'score', 'score', section.score]);
    rows.push([
      'section',
      section.title,
      'max_score',
      'max_score',
      section.max_score,
    ]);
    rows.push([
      'section',
      section.title,
      'percentage',
      'percentage',
      section.percentage,
    ]);
    rows.push([
      'section',
      section.title,
      'description',
      'description',
      getLocalizedString(section.description ?? ''),
    ]);

    section.groups.forEach((group) => {
      rows.push(['group', section.title, group.title, 'score', group.score]);
      rows.push([
        'group',
        section.title,
        group.title,
        'max_score',
        group.max_score,
      ]);
      rows.push([
        'group',
        section.title,
        group.title,
        'percentage',
        group.percentage,
      ]);
    });

    section.exposed_answers.forEach((answer) => {
      rows.push([
        'answer',
        section.title,
        getCsvCellValue(answer.question_title),
        answer.question_key,
        answer.value,
      ]);
    });

    section.file_answers.forEach((file) => {
      rows.push([
        'file',
        section.title,
        getCsvCellValue(file.question_title),
        file.question_key,
        file.file_value,
      ]);
    });
  });

  return [csvHeaders, ...rows]
    .map((row) => row.map(escapeCsvValue).join(','))
    .join('\n');
};

export const AssessmentReportNewDisplay = ({
  report,
}: AssessmentReportDisplayNewProps) => {
  const { meta, sections, created } = report;
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const reportIntroRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    if (!meta?.supplier_name || !meta.template_name) return;

    document.title = `${meta.supplier_name} - ${meta.template_name} report | SupplyPassport App`;
  }, [meta?.supplier_name, meta?.template_name]);

  if (!meta) {
    return (
      <Container className="mt-4">
        <p>Report metadata is missing. Unable to display report.</p>
      </Container>
    );
  }

  const handleDownloadClick = async (fileValue: string) => {
    if (!fileValue) return;

    try {
      setGeneratingLink(true);

      const { data, error } = await supabase.storage
        .from('assessment-answers')
        .createSignedUrl(fileValue, 60 * 60); // valid for 1 hour

      if (error) {
        captureSupabaseError(error, { op: 'storage.createSignedUrl' });
        console.error('Error creating signed URL:', error);
        setToastMessage(
          'There was an error generating the download link. Please try again later.',
        );
        setShowToast(true);
        return;
      }

      window.open(data.signedUrl, '_blank');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handlePdfDownload = async () => {
    if (generatingPdf) return;

    try {
      setGeneratingPdf(true);
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const availableWidth = pageWidth - margin * 2;
      const availableHeight = pageHeight - margin * 2;

      const renderPage = async (
        element: HTMLDivElement,
        addNewPage = false,
      ) => {
        const canvas = await html2canvas(element, {
          scale: PDF_RENDER_SCALE,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
        });

        const imageData = canvas.toDataURL('image/jpeg', PDF_IMAGE_QUALITY);
        const scale = Math.min(
          availableWidth / canvas.width,
          availableHeight / canvas.height,
        );
        const imageWidth = canvas.width * scale;
        const imageHeight = canvas.height * scale;
        const x = (pageWidth - imageWidth) / 2;

        if (addNewPage) {
          pdf.addPage();
        }

        pdf.addImage(
          imageData,
          'JPEG',
          x,
          margin,
          imageWidth,
          imageHeight,
          undefined,
          'MEDIUM',
        );
      };

      if (reportIntroRef.current) {
        await renderPage(reportIntroRef.current);
      }

      for (const sectionRef of sectionRefs.current) {
        if (!sectionRef) continue;
        await renderPage(sectionRef, true);
      }

      pdf.save(
        `${meta.supplier_name || 'assessment-report'}-${new Date(created || Date.now()).toISOString().slice(0, 10)}.pdf`,
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      setToastMessage(
        'There was an error generating the PDF. Please try again later.',
      );
      setShowToast(true);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleCsvDownload = () => {
    const csvContent = buildCsvRows(report);
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const createdDate = new Date(created || Date.now())
      .toISOString()
      .slice(0, 10);

    link.href = url;
    link.download = `${meta.supplier_name || 'assessment-report'}-${createdDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

  const barChartData = {
    labels: [
      meta.supplier_name,
      'Similar suppliers Avg',
      'Industry Avg',
      'All suppliers Avg',
    ],
    datasets: [
      {
        label: 'ESG Score',
        data: [
          meta.overall_percentage,
          meta.size_average_percentage ?? 64,
          meta.industry_average_percentage ?? 72,
          meta.others_average ?? 65,
        ],
        backgroundColor: ['#103b66', '#cbd5e1', '#cbd5e1', '#cbd5e1'],
      },
    ],
  };

  const chartOptions: ChartOptions<'bar'> = {
    indexAxis: 'y',
    responsive: true,
    animation: false,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#0f172a',
        titleColor: '#f8fafc',
        bodyColor: '#e2e8f0',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            size: 13,
            family: 'Lexend, Helvetica, sans-serif',
            weight: 600,
          },
          color: '#334155',
        },
        grid: { display: false },
        border: { display: false },
      },
      x: {
        min: 0,
        max: 100,
        ticks: {
          font: {
            size: 12,
            family: 'Lexend, Helvetica, sans-serif',
            weight: 600,
          },
          color: '#64748b',
          callback: (value) => `${value}%`,
        },
        grid: { color: '#e2e8f0' },
        border: { display: false },
      },
    },
  };

  const overallPercentage = getTotalScorePercentage(
    meta.total_score,
    meta.max_score,
  );

  return (
    <>
      <div
        className="fixed-top"
        style={{ ...reportStyles.topBar, zIndex: 1000 }}
      >
        <Container>
          {!isPrinting && (
            <div className="d-flex justify-content-end my-2">
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="light"
                  id="export-dropdown"
                  disabled={generatingPdf}
                >
                  <i className="bi bi-download me-2" />
                  {generatingPdf ? 'Generating PDF...' : 'Export'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={handlePdfDownload}>PDF</Dropdown.Item>
                  <Dropdown.Item onClick={handleCsvDownload}>CSV</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
        </Container>
      </div>
      <div style={reportStyles.shell}>
        <Container className="mt-5 pt-4 pb-5" style={{ maxWidth: '1240px' }}>
          <div>
            <div ref={reportIntroRef}>
              <Card style={reportStyles.heroCard} className="mb-4">
                <div style={reportStyles.heroHeader} className="p-4 p-md-5">
                  <Row className="g-4 align-items-start">
                    <Col lg={8}>
                      <div style={reportStyles.eyebrow}>
                        Supplier Assessment Report
                      </div>
                      <img
                        src={logo}
                        alt="SupplyPassport Logo"
                        height="52"
                        className="mt-3 mb-4"
                      />
                      <h1
                        className="mb-2 fw-semibold"
                        style={{ fontSize: '2.2rem' }}
                      >
                        {meta.supplier_name}
                      </h1>
                      <div
                        className="mb-3"
                        style={{
                          color: 'rgba(241,245,249,0.82)',
                          fontSize: '1.08rem',
                        }}
                      >
                        {meta.template_name}
                      </div>
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        <span style={reportStyles.badge}>
                          <i className="bi bi-calendar4-week" />
                          Assessment Date: {formatDate(created)}
                        </span>
                        <span style={reportStyles.badge}>
                          <i className="bi bi-clipboard-data" />
                          Sections Reviewed: {sections.length}
                        </span>
                      </div>
                      <p
                        className="mb-0"
                        style={{
                          color: 'rgba(241,245,249,0.86)',
                          maxWidth: '60rem',
                        }}
                      >
                        {getPreambleText(meta.tags, meta.supplier_name)}
                      </p>
                    </Col>
                    <Col lg={4}>
                      <div
                        className="p-4"
                        style={{
                          borderRadius: '22px',
                          background: 'rgba(255,255,255,0.08)',
                          border: '1px solid rgba(255,255,255,0.12)',
                        }}
                      >
                        <div
                          style={{
                            ...reportStyles.metricLabel,
                            color: '#cbd5e1',
                          }}
                        >
                          Overall Performance
                        </div>
                        <div
                          className="mt-3 mx-auto"
                          style={{ maxWidth: '220px' }}
                        >
                          <CircularProgressbar
                            value={overallPercentage}
                            text={`${overallPercentage}%`}
                            styles={buildStyles({
                              pathColor: getBarColor(overallPercentage),
                              trailColor: 'rgba(255,255,255,0.18)',
                              textColor: '#f8fafc',
                              textSize: '16px',
                              strokeLinecap: 'butt',
                            })}
                          />
                        </div>
                        <div
                          className="text-center mt-3"
                          style={{ color: '#cbd5e1' }}
                        >
                          Weighted assessment score
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
                <Card.Body className="p-4 p-md-5">
                  <Row className="g-3 mb-4">
                    <Col md={4}>
                      <SummaryMetric
                        label="Assessment Score"
                        value={`${overallPercentage}%`}
                        accent="#103b66"
                      />
                    </Col>
                    <Col md={4}>
                      <SummaryMetric
                        label="Points Achieved"
                        value={`${meta.total_score ?? 0} / ${meta.max_score ?? 0}`}
                      />
                    </Col>
                    <Col md={4}>
                      <SummaryMetric
                        label="Benchmark Delta"
                        value={`${Math.round(overallPercentage - (meta.industry_average_percentage ?? 72))}% vs industry`}
                      />
                    </Col>
                  </Row>
                  <Card
                    className="border-0"
                    style={{ background: '#f8fafc', borderRadius: '20px' }}
                  >
                    <Card.Body className="p-4">
                      <div className="mb-3">
                        <div style={reportStyles.subSectionTitle}>
                          Benchmark Comparison
                        </div>
                        <h3 className="mb-0 mt-1" style={{ color: '#0f172a' }}>
                          Relative ESG positioning
                        </h3>
                      </div>
                      <div
                        style={{ maxHeight: '320px' }}
                        className="d-flex justify-content-center"
                      >
                        <Bar data={barChartData} options={chartOptions} />
                      </div>
                    </Card.Body>
                  </Card>
                </Card.Body>
              </Card>
            </div>
            <Container fluid className="px-0">
              <Row className="mt-2">
                <Col>
                  <div>
                    {sections.map((section, index) => (
                      <Card
                        key={section.id}
                        className="mb-4"
                        style={reportStyles.sectionCard}
                        ref={(element) => {
                          sectionRefs.current[index] = element;
                        }}
                      >
                        <div style={reportStyles.sectionHeader} className="p-4">
                          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                            <div>
                              <div style={reportStyles.subSectionTitle}>
                                Section Analysis
                              </div>
                              <h2
                                className="mb-1 mt-1"
                                style={{ color: '#0f172a' }}
                              >
                                {section.title}
                              </h2>
                              {section.description && (
                                <div
                                  style={{ color: '#475569' }}
                                  dangerouslySetInnerHTML={{
                                    __html: getLocalizedString(
                                      section.description,
                                    ),
                                  }}
                                ></div>
                              )}
                            </div>
                            <span style={reportStyles.badge}>
                              <i className="bi bi-speedometer2" />
                              {Math.round(
                                getTotalScorePercentage(
                                  section.score,
                                  section.max_score,
                                ),
                              )}
                              % score
                            </span>
                          </div>
                        </div>
                        <Card.Body className="p-4 p-md-5">
                          <Row className="g-4">
                            <Col
                              xs={12}
                              md={4}
                              className="d-flex flex-column align-items-center"
                            >
                              <div
                                style={{
                                  maxWidth: '210px',
                                  width: '100%',
                                }}
                                className="d-flex flex-column align-items-center p-2"
                              >
                                <CircularProgressbar
                                  value={getTotalScorePercentage(
                                    section.score,
                                    section.max_score,
                                  )}
                                  text={`${Math.round(
                                    getTotalScorePercentage(
                                      section.score,
                                      section.max_score,
                                    ),
                                  )}%`}
                                  styles={buildStyles({
                                    pathColor: getBarColor(
                                      getTotalScorePercentage(
                                        section.score,
                                        section.max_score,
                                      ),
                                    ),
                                    trailColor: '#e2e8f0',
                                    textColor: '#111827',
                                    textSize: '16px',
                                    strokeLinecap: 'butt',
                                  })}
                                />
                                <h5 className="mt-3">Section Score</h5>
                              </div>
                            </Col>
                            <Col
                              xs={12}
                              md={8}
                              className="d-flex align-items-center"
                            >
                              <Row className="g-3 w-100">
                                {/* <Col sm={6}>
                                  <SummaryMetric
                                    label="Section Percentage"
                                    value={`${section.percentage ?? Math.round(getTotalScorePercentage(section.score, section.max_score))}%`}
                                  />
                                </Col>
                                <Col sm={6}>
                                  <SummaryMetric
                                    label="Question Groups"
                                    value={String(section.groups.length)}
                                  />
                                </Col> */}
                                <Col sm={6}>
                                  <SummaryMetric
                                    label="Total Points"
                                    value={String(
                                      `${section.score} of ${section.max_score} points`,
                                    )}
                                  />
                                </Col>
                                <Col sm={6}>
                                  <SummaryMetric
                                    label="Attached Files"
                                    value={String(
                                      section.file_answers.filter((file) =>
                                        Boolean(file.file_value),
                                      ).length,
                                    )}
                                  />
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                          {section.groups.length > 0 && (
                            <div className="mt-4">
                              <div
                                className="mb-2"
                                style={reportStyles.subSectionTitle}
                              >
                                Group Scores
                              </div>
                              <div style={reportStyles.tableShell}>
                                <Table className="mb-0 align-middle">
                                  <tbody>
                                    {section.groups.map((group, gIdx) => (
                                      <tr key={gIdx}>
                                        <td className="fw-medium">
                                          {group.title}
                                        </td>
                                        <td style={{ width: '120px' }}>
                                          <ScoreIcon value={group.percentage} />
                                          <span className="ms-2">
                                            {group.percentage}%
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            </div>
                          )}
                          {section.exposed_answers.length > 0 && (
                            <div className="mt-4">
                              <div
                                className="mb-2"
                                style={reportStyles.subSectionTitle}
                              >
                                Key Answers
                              </div>
                              <div style={reportStyles.tableShell}>
                                <Table bordered className="mb-0 align-middle">
                                  <tbody>
                                    {section.exposed_answers.map(
                                      (answer, aIdx) => (
                                        <tr key={aIdx}>
                                          <td className="fw-medium">
                                            {answer.question_title}
                                          </td>
                                          <td>{String(answer.value)}</td>
                                        </tr>
                                      ),
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                            </div>
                          )}
                          {section.file_answers.length > 0 && (
                            <div className="mt-4">
                              <div
                                className="mb-2"
                                style={reportStyles.subSectionTitle}
                              >
                                Attached Files
                              </div>
                              <div style={reportStyles.tableShell}>
                                <Table className="mb-0 align-middle">
                                  <tbody>
                                    {section.file_answers.map((file, fIdx) => {
                                      const questionTitle =
                                        file.question_title || 'Untitled';
                                      return file.file_value ? (
                                        <tr key={fIdx}>
                                          <td className="fw-medium">
                                            {questionTitle}
                                          </td>
                                          <td className="d-flex justify-content-end">
                                            {isPrinting || generatingPdf ? (
                                              <a
                                                href={file.file_value}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >
                                                View File
                                              </a>
                                            ) : (
                                              <Button
                                                size="sm"
                                                variant="primary"
                                                data-testid="download-file-btn"
                                                onClick={() => {
                                                  handleDownloadClick(
                                                    file.file_value,
                                                  );
                                                }}
                                                disabled={generatingLink}
                                              >
                                                <i className="bi bi-cloud-download me-2" />{' '}
                                                Download
                                              </Button>
                                            )}
                                          </td>
                                        </tr>
                                      ) : null;
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </Col>
              </Row>
            </Container>
            <Modal show={showToast} onHide={() => setShowToast(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>Notification</Modal.Title>
              </Modal.Header>
              <Modal.Body>{toastMessage}</Modal.Body>
              <Modal.Footer>
                <Button variant="primary" onClick={() => setShowToast(false)}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
            <p
              className="text-center text-muted mt-4 mb-0"
              style={{ fontSize: '0.75rem' }}
            >
              This report is generated based on self-reported data provided by
              the assessed entity. SupplyPassport has not independently verified
              or audited this information and assumes no responsibility for its
              accuracy or completeness. For more information, visit{' '}
              <a href="https://www.supplypassport.co">www.supplypassport.co</a>
            </p>
          </div>
        </Container>
      </div>
      <div id="pdf-render-complete" style={{ display: 'none' }} />
    </>
  );
};
